import { Auth0TokenRoleKey } from '@eigtech/auth0-types'
import { whitelabelConfig } from '@/lib/constants/whitelabelConfig'
import { Role, useAuth0, useAuthStore } from '@eigtech/ui-shared-auth'
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  ComposedAlert,
  Container,
  Flex,
  Stack,
  useDisclosure,
} from '@eigtech/ui-shared-dave'
import { IS_DEV } from '@eigtech/ui-shared-environment-web'
import { Outlet } from '@eigtech/ui-shared-router'
import { ReactNode, Suspense } from 'react'
import { useGetDelegateUserActiveContacts } from '../../../api'
import { Navigation, TopToolbar } from '../../Toolbars'
import { EstimatorLicenseDrawer } from '../EstimatorLicense'
import { PostAuthProviders } from './PostAuthProviders'

const STAGE_NAME = import.meta.env.VITE_STAGE_NAME
if (IS_DEV) {
  document.title = `${whitelabelConfig.title} - LOCAL DEV`
} else if (typeof STAGE_NAME === 'string' && !STAGE_NAME.startsWith('prod')) {
  document.title = `${whitelabelConfig.title} - ${STAGE_NAME}`
} else {
  document.title = whitelabelConfig.title
}

export function AppContainer({ children }: { children: ReactNode }) {
  const hasSetAccessToken = useAuthStore((state) => state.hasSetAccessToken)

  return (
    <>
      <Flex w="full">
        {hasSetAccessToken && (
          <Navigation
            display={{
              base: 'none',
              md: 'flex',
            }}
            flexShrink={0}
            zIndex={2}
          />
        )}
        <Box flex="1" minW="0px" pb="20">
          {hasSetAccessToken && (
            <>
              <TopToolbar />
              <LicenseAlert />
            </>
          )}
          <Box position="relative">
            <Suspense>
              {hasSetAccessToken ? <PostAuthProviders>{children}</PostAuthProviders> : children}
            </Suspense>
          </Box>
        </Box>
      </Flex>
    </>
  )
}

function LicenseAlert() {
  const { user } = useAuth0()
  const { contacts, isContactsPending } = useGetDelegateUserActiveContacts()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const roles = user?.[Auth0TokenRoleKey] as Role[] | undefined
  const isEstimator = roles?.includes('Estimator - User')

  const hasLicenseNumber = contacts.some((contact) => !!contact.metadata.licenseNumber)

  if (!isEstimator || isContactsPending || hasLicenseNumber) {
    return null
  }

  return (
    <>
      <Alert status="warning">
        <AlertIcon />
        <Stack
          alignItems={{ base: 'stretch', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          justifyContent="space-between"
          w="full"
        >
          <AlertTitle>You have not set your license number yet</AlertTitle>
          <Button onClick={onOpen}>Update License Number</Button>
        </Stack>
      </Alert>

      {isOpen && <EstimatorLicenseDrawer isOpen={isOpen} onClose={onClose} />}
    </>
  )
}

function App() {
  const { error } = useAuth0()

  if (error) {
    return (
      <Container maxW="container.text">
        <ComposedAlert
          alert={{
            title: 'Auth Error',
            description:
              'Something went wrong authenticating you. Please try again. If the issue persists, please contact us.',
          }}
          shadow="md"
          status="error"
        />
      </Container>
    )
  }

  return (
    <AppContainer>
      <Outlet />
    </AppContainer>
  )
}

export default App
